import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { v4 as uuidV4 } from "uuid";
import { firestore, storage, FieldValue } from "../../config/firebaseConfig";
import { readingResult, computeOutcome } from "../../utils/uttMinWall";
import { allocateReportNumber } from "../../utils/reportNumbers";

// Restarted UTT log on Firestore (`uttLogs`). One document per inspected item,
// report-level fields denormalized onto each entry (same shape the legacy
// RTDB log used, plus the readiness contract fields: assetNumber, loggedAt,
// outcome). The legacy `logs/utt/items` path stays readable but read-only.

let unsubscribe = null;
let currentLimit = 0;

// Windowed listener: the collection holds the full backfilled history, so
// loading it whole freezes first paint. Fetch the most recent N (ordered by
// loggedAt) and let the page widen the window on demand.
export const DEFAULT_LOG_WINDOW = 1000;
export const LOG_WINDOW_STEP = 2000;

export const fetchUttLogsFsAsync = createAsyncThunk(
  "uttLogs/fetch",
  async (limitArg, thunkAPI) => {
    const limit = limitArg || DEFAULT_LOG_WINDOW;
    if (unsubscribe && limit <= currentLimit) return;
    if (unsubscribe) unsubscribe();
    currentLimit = limit;
    thunkAPI.dispatch(setLoading(true));
    unsubscribe = firestore
      .collection("uttLogs")
      .orderBy("loggedAt", "desc")
      .limit(limit)
      .onSnapshot(
        (snapshot) => {
          const items = {};
          snapshot.forEach((doc) => {
            const data = doc.data();
            if (!data.deleted) items[doc.id] = data;
          });
          thunkAPI.dispatch(
            setUttLogs({
              items,
              windowSize: limit,
              reachedEnd: snapshot.size < limit,
            })
          );
        },
        (error) => {
          console.error("uttLogs listener error", error);
          thunkAPI.dispatch(setError(error.message));
        }
      );
  }
);

export const cancelFetchUttLogsFs = createAsyncThunk(
  "uttLogs/cancel",
  async () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
      currentLimit = 0;
    }
  }
);

// Targeted per-asset history (equality query — no composite index needed).
// Used by Asset Lookup so it never depends on the windowed grid data.
export const fetchAssetUttEntriesAsync = createAsyncThunk(
  "uttLogs/fetchAssetEntries",
  async (assetNumber, thunkAPI) => {
    try {
      const snapshot = await firestore
        .collection("uttLogs")
        .where("assetNumber", "==", assetNumber)
        .get();
      const entries = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (!data.deleted) entries.push({ id: doc.id, ...data });
      });
      return entries;
    } catch (error) {
      console.error("fetchAssetUttEntries failed", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Full-history search: the windowed listener only covers the most recent
// entries, so grid filters can't see older history. This queries Firestore
// directly — equality on asset/report needs no index; a date range alone
// runs as a server range on `date` (yyyy-MM-dd strings sort lexically).
// Secondary criteria are applied client-side to stay index-free.
const SEARCH_CAP = 5000;

export const uttSearchMatch = (
  data,
  { assetNumber = "", report = "", dateFrom = "", dateTo = "" }
) => {
  if (data.deleted) return false;
  const asset = assetNumber.trim();
  const rep = report.trim();
  if (asset && (data.assetNumber || "") !== asset) return false;
  if (rep && (data.report || "") !== rep) return false;
  if (dateFrom && (data.date || "") < dateFrom) return false;
  if (dateTo && (data.date || "") > dateTo) return false;
  return true;
};

export const searchUttLogsAsync = createAsyncThunk(
  "uttLogs/search",
  async (criteria, thunkAPI) => {
    try {
      const asset = (criteria.assetNumber || "").trim();
      const rep = (criteria.report || "").trim();
      const { dateFrom = "", dateTo = "" } = criteria;
      let query = firestore.collection("uttLogs");
      let capped = false;
      if (asset) {
        query = query.where("assetNumber", "==", asset);
      } else if (rep) {
        query = query.where("report", "==", rep);
      } else if (dateFrom || dateTo) {
        if (dateFrom) query = query.where("date", ">=", dateFrom);
        if (dateTo) query = query.where("date", "<=", dateTo);
        query = query.orderBy("date", "desc").limit(SEARCH_CAP);
      } else {
        return thunkAPI.rejectWithValue("Enter something to search for");
      }
      const snapshot = await query.get();
      capped = !asset && !rep && snapshot.size >= SEARCH_CAP;
      const items = {};
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (uttSearchMatch(data, criteria)) items[doc.id] = data;
      });
      return { items, capped };
    } catch (error) {
      console.error("searchUttLogs failed", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Report number sequencing (same hard-won contract as MPI): the counter is
// a Firestore doc advanced ONLY by auto-numbered saves, via a monotonic
// transaction. Manual report numbers never touch it.
const REPORT_COUNTER_REF = () =>
  firestore.collection("counters").doc("uttReportNumber");

export const fetchNextUttReportNumberAsync = createAsyncThunk(
  "uttLogs/fetchNextReportNumber",
  async (_, thunkAPI) => {
    try {
      const doc = await REPORT_COUNTER_REF().get();
      const last = doc.exists ? Number(doc.data().last) : NaN;
      return Number.isNaN(last) ? null : String(last + 1);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Uniqueness check for manually entered report numbers.
export const checkUttReportExistsAsync = createAsyncThunk(
  "uttLogs/checkReportExists",
  async (report, thunkAPI) => {
    try {
      const snapshot = await firestore
        .collection("uttLogs")
        .where("report", "==", report)
        .limit(1)
        .get();
      return !snapshot.empty;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const uttReportExists = async (reportNumber) => {
  const snapshot = await firestore
    .collection("uttLogs")
    .where("report", "==", reportNumber)
    .limit(1)
    .get();
  return !snapshot.empty;
};

// Take the next number inside a counter transaction. Two saves racing for
// the same number contend on the counter doc, so one of them retries and
// gets the number after.
const reserveUttReportNumber = () =>
  allocateReportNumber({
    reserve: () =>
      firestore.runTransaction(async (tx) => {
        const counterRef = REPORT_COUNTER_REF();
        const doc = await tx.get(counterRef);
        const last = doc.exists ? Number(doc.data().last) : NaN;
        if (Number.isNaN(last)) return null;
        tx.set(counterRef, { last: last + 1 }, { merge: true });
        return last + 1;
      }),
    isTaken: uttReportExists,
  });

// Persist a whole report: optional PDF upload, then one uttLogs doc per item.
// Each reading keeps the minWall it was judged against (snapshot, not lookup).
// `removedIds` are entries the report had when opened and no longer has:
// they're marked deleted in the same batch, the way the log deletes
// anything, so every reader skips them. An edit can remove every item,
// which takes the report off the log.
//
// Auto numbers are reserved HERE, not when the dialog opened: reading the
// counter on open and bumping it after the save let Houma and Broussard,
// saving at the same time, share one number and merge their reports. The
// number the dialog previewed is only a hint; the payload carries the one
// actually written so the page can say when they differ.
export const saveUttReportAsync = createAsyncThunk(
  "uttLogs/saveReport",
  async (
    { report, items, removedIds = [], pdfFile, user, autoNumber },
    thunkAPI
  ) => {
    try {
      const requested = String(report.report || "").trim();
      const isNewReport =
        items.every((item) => !item.id) && removedIds.length === 0;
      let reportNumber = requested;
      if (autoNumber) {
        reportNumber = await reserveUttReportNumber();
      } else if (isNewReport) {
        if (!reportNumber) throw new Error("Enter a report number");
        if (await uttReportExists(reportNumber)) {
          throw new Error(`Report number ${reportNumber} already exists`);
        }
      }

      let pdfUrl = report.pdfUrl || "";
      let pdfPath = report.pdfPath || "";
      if (pdfFile) {
        pdfPath = `utt/reportPdfs/${uuidV4()}/${pdfFile.name}`;
        const snapshot = await storage.ref(pdfPath).put(pdfFile);
        pdfUrl = await snapshot.ref.getDownloadURL();
      }

      const batch = firestore.batch();
      items.forEach((item) => {
        const { id, ...entry } = item;
        const ref = id
          ? firestore.collection("uttLogs").doc(id)
          : firestore.collection("uttLogs").doc();
        const readings = (entry.readings || [])
          .filter((r) => r.value !== "" && r.value !== null)
          .map((r) => ({
            locationId: r.locationId || null,
            label: r.label || "",
            value: Number(r.value),
            minWall:
              r.minWall === null || r.minWall === undefined
                ? null
                : Number(r.minWall),
            oemDocId: r.oemDocId || null,
            source: r.source || null,
            minSource: r.minSource || null,
            result: readingResult(r),
          }));
        batch.set(
          ref,
          {
            ...entry,
            readings,
            outcome: computeOutcome(readings, entry.visual),
            report: reportNumber,
            inspector: report.inspector,
            date: report.date,
            amPm: report.amPm || "",
            location: report.location || "",
            notes: report.notes || "",
            pdfUrl,
            pdfPath,
            deleted: false,
            loggedAt: entry.loggedAt || FieldValue.serverTimestamp(),
            loggedBy: entry.loggedBy || {
              uid: user?.uid || "",
              name: user?.name || "",
            },
            updatedAt: FieldValue.serverTimestamp(),
          },
          { merge: true }
        );
      });
      removedIds.forEach((id) => {
        batch.set(
          firestore.collection("uttLogs").doc(id),
          {
            deleted: true,
            deletedBy: { uid: user?.uid || "", name: user?.name || "" },
            updatedAt: FieldValue.serverTimestamp(),
          },
          { merge: true }
        );
      });
      await batch.commit();

      return {
        report: reportNumber,
        requested,
        renumbered:
          Boolean(autoNumber) &&
          /^\d+$/.test(requested) &&
          reportNumber !== requested,
        removed: removedIds.length,
        kept: items.length,
      };
    } catch (error) {
      console.error("saveUttReport failed", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Attach a PDF to every entry sharing a report number (report-scoped, like
// the MPI cell but across the whole report group).
export const attachReportPdfAsync = createAsyncThunk(
  "uttLogs/attachReportPdf",
  async ({ report, file }, thunkAPI) => {
    try {
      const pdfPath = `utt/reportPdfs/${uuidV4()}/${file.name}`;
      const snap = await storage.ref(pdfPath).put(file);
      const pdfUrl = await snap.ref.getDownloadURL();
      const query = await firestore
        .collection("uttLogs")
        .where("report", "==", report)
        .get();
      const batch = firestore.batch();
      query.forEach((doc) =>
        batch.update(doc.ref, {
          pdfUrl,
          pdfPath,
          updatedAt: FieldValue.serverTimestamp(),
        })
      );
      await batch.commit();
      return { report, pdfUrl, count: query.size };
    } catch (error) {
      console.error("attachReportPdf failed", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Detach the PDF link from every entry on a report. The Storage object is
// left in place (audit trail); only the references clear.
export const detachReportPdfAsync = createAsyncThunk(
  "uttLogs/detachReportPdf",
  async ({ report }, thunkAPI) => {
    try {
      const query = await firestore
        .collection("uttLogs")
        .where("report", "==", report)
        .get();
      const batch = firestore.batch();
      query.forEach((doc) =>
        batch.update(doc.ref, {
          pdfUrl: "",
          pdfPath: "",
          updatedAt: FieldValue.serverTimestamp(),
        })
      );
      await batch.commit();
      return { report, count: query.size };
    } catch (error) {
      console.error("detachReportPdf failed", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteUttLogAsync = createAsyncThunk(
  "uttLogs/delete",
  async (id, thunkAPI) => {
    try {
      await firestore
        .collection("uttLogs")
        .doc(id)
        .set({ deleted: true }, { merge: true });
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const uttLogsSlice = createSlice({
  name: "uttLogs",
  initialState: {
    items: {},
    error: null,
    saving: false,
    // { report, requested, renumbered, removed, kept } of the last
    // successful save, so the page can say which number a report actually
    // got and what came off it.
    lastSave: null,
    loading: false,
    windowSize: 0,
    reachedEnd: true,
    search: { items: null, searching: false, capped: false, error: null },
  },
  reducers: {
    setUttLogs(state, action) {
      state.items = action.payload.items;
      state.windowSize = action.payload.windowSize;
      state.reachedEnd = action.payload.reachedEnd;
      state.loading = false;
      state.error = null;
    },
    clearUttSearch(state) {
      state.search = {
        items: null,
        searching: false,
        capped: false,
        error: null,
      };
    },
    clearLastSave(state) {
      state.lastSave = null;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchUttLogsAsync.pending, (state) => {
        state.search.searching = true;
        state.search.error = null;
      })
      .addCase(searchUttLogsAsync.fulfilled, (state, action) => {
        state.search.searching = false;
        state.search.items = action.payload.items;
        state.search.capped = action.payload.capped;
      })
      .addCase(searchUttLogsAsync.rejected, (state, action) => {
        state.search.searching = false;
        state.search.error = action.payload;
      })
      .addCase(saveUttReportAsync.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveUttReportAsync.fulfilled, (state, action) => {
        state.saving = false;
        state.error = null;
        state.lastSave = action.payload;
      })
      .addCase(saveUttReportAsync.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  },
});

export const {
  setUttLogs,
  setLoading,
  setError,
  clearUttSearch,
  clearLastSave,
} = uttLogsSlice.actions;

export default uttLogsSlice.reducer;
